import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Stack,
  Typography,
  useTheme,
  alpha,
  Paper,
  Button,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MdTimer, MdArrowForward } from "react-icons/md";

import { fetchAttendanceRecords } from "store/features/company/attendanceManagement/attendanceRecords/attendanceRecordsActions";
import {
  selectAttendanceRecords,
  selectAttendanceRecordsLoading,
} from "store/features/company/attendanceManagement/attendanceRecords/attendanceRecordsSelectors";
import { fetchEmployees } from "store/features/company/employee/employeeActions";
import { selectEmployees } from "store/features/company/employee/employeeSelectors";
import { fetchTopPriorityTasks } from "store/features/company/projectManagement/task/taskActions";
import { selectTopPriorityTasks } from "store/features/company/projectManagement/task/taskSelectors";
import { fetchProjects } from "store/features/company/projectManagement/project/projectActions";
import { selectProjects } from "store/features/company/projectManagement/project/projectSelectors";
import { fetchMyLeaves as fetchAllLeaves } from "store/features/company/leaveManagement/leaveDetails/leaveDetailsActions";
import { selectLeaves as selectAllLeaves } from "store/features/company/leaveManagement/leaveDetails/leaveDetailsSelectors";
import { fetchMyLeaves as fetchMyOwnLeaves } from "store/features/company/leaveManagement/applyLeave/applyLeaveActions";
import { selectMyLeaves } from "store/features/company/leaveManagement/applyLeave/applyLeaveSelectors";
import { fetchRequisitions } from "store/features/company/requisition/requisitionActions";
import { selectRequisitions } from "store/features/company/requisition/requisitionSelectors";
import { selectCurrentUser } from "store/features/auth/authSelectors";
import usePermissions from "hooks/usePermissions";
import { getSecureLocalItem } from "utils/secureSessionStorage";

import HeaderBanner from "../../../components/widget/HeaderBanner";
import AttendanceQuickView from "../../../components/widget/AttendanceQuickView";
import MyAttendanceCard from "../../../components/widget/MyAttendanceCard";
import PendingApprovalsWidget from "../../../components/widget/PendingApprovalsWidget";
import UpcomingHolidaysWidget from "../../../components/widget/UpcomingHolidaysWidget";
import QuickShortcutsBar from "../../../components/widget/QuickShortcutsBar";
import TopPriorityTasks from "../../../components/widget/TopPriorityTasks";
import EmployeeInfoCard from "../../../components/widget/EmployeeInfoCard";
import EmployeeQuickActionsCard from "../../../components/widget/EmployeeQuickActionsCard";
import TaskStatsWidget from "../../../components/widget/TaskStatsWidget";
import ActiveProjectsWidget from "../../../components/widget/ActiveProjectsWidget";

const CompanyDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDark = theme.palette.mode === "dark";

  const [attendanceStatus, setAttendanceStatus] = useState(null);

  const { hasPermission, isFeatureEnabled } = usePermissions();
  const currentUser = useSelector(selectCurrentUser);
  const userRole =
    getSecureLocalItem("role") ||
    currentUser?.role ||
    currentUser?.role_name ||
    "Company";
  const isEmployeeRole = userRole?.toLowerCase() === "employee";
  const isCompanyRole = userRole?.toLowerCase() === "company";

  const hasAttendanceFeature = isFeatureEnabled("attendance");
  const hasProjectsFeature = isFeatureEnabled("projects");
  const hasLeaveFeature = isFeatureEnabled("leave");
  const hasEmployeesFeature = isFeatureEnabled("employees");

  const employeeCompanies = useMemo(() => {
    const raw = getSecureLocalItem("employee_companies");
    try {
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }, []);
  const employeeCompany = employeeCompanies[0] || null;

  const canViewAllAttendance =
    hasPermission("attendance_records.view") ||
    hasPermission("attendance.view") ||
    !isEmployeeRole;
  const showAttendanceQuickView =
    hasAttendanceFeature && (canViewAllAttendance || isCompanyRole);
  const showMyAttendanceCard = hasAttendanceFeature && !isCompanyRole;

  const canApproveLeave =
    hasLeaveFeature &&
    (hasPermission("leave_details.approve") ||
      hasPermission("leave_details.view") ||
      hasPermission("leave_details.edit") ||
      hasPermission("leave_details.action") ||
      isCompanyRole);

  const canApplyLeave =
    hasLeaveFeature &&
    (hasPermission("apply_leave.view") ||
      hasPermission("apply_leave.create") ||
      isEmployeeRole);

  const attendanceRecords = useSelector(selectAttendanceRecords) || [];
  const attendanceLoading = useSelector(selectAttendanceRecordsLoading);
  const employees = useSelector(selectEmployees) || [];
  const topPriorityTasks = useSelector(selectTopPriorityTasks) || [];
  const projects = useSelector(selectProjects) || [];
  const allLeaves = useSelector(selectAllLeaves) || [];
  const myLeaves = useSelector(selectMyLeaves) || [];
  const leaves = canApproveLeave ? allLeaves : canApplyLeave ? myLeaves : [];
  const requisitions = useSelector(selectRequisitions) || [];

  useEffect(() => {
    const token = getSecureLocalItem("token");
    if (!token) return;

    const today = new Date().toISOString().split("T")[0];
    const tenDaysAgo = new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];

    if (
      hasAttendanceFeature &&
      (showAttendanceQuickView || showMyAttendanceCard)
    ) {
      dispatch(
        fetchAttendanceRecords({
          page: 1,
          limit: 200,
          from_date: tenDaysAgo,
          to_date: today,
        }),
      ).catch(() => {});
    }
    if (
      hasEmployeesFeature &&
      (hasPermission("employee.view") || !isEmployeeRole)
    ) {
      dispatch(fetchEmployees()).catch(() => {});
    }
    if (
      hasProjectsFeature &&
      (hasPermission("task.view") || hasPermission("projects.view"))
    ) {
      dispatch(fetchTopPriorityTasks()).catch(() => {});
      dispatch(fetchProjects()).catch(() => {});
    }
    if (hasLeaveFeature) {
      if (canApproveLeave) dispatch(fetchAllLeaves()).catch(() => {});
      else if (canApplyLeave) dispatch(fetchMyOwnLeaves()).catch(() => {});
    }
    if (hasEmployeesFeature) dispatch(fetchRequisitions()).catch(() => {});
  }, [
    dispatch,
    isEmployeeRole,
    showAttendanceQuickView,
    showMyAttendanceCard,
    canApproveLeave,
    canApplyLeave,
    hasAttendanceFeature,
    hasProjectsFeature,
    hasLeaveFeature,
    hasEmployeesFeature,
  ]);

  const myTodayRecord = useMemo(() => {
    if (!currentUser) return null;
    const userId =
      currentUser.id || currentUser.user_id || currentUser.employee_id;
    return (
      attendanceRecords.find(
        (r) =>
          r.user_id === userId ||
          r.employee_id === userId ||
          r.email === currentUser.email,
      ) ||
      attendanceRecords[0] ||
      null
    );
  }, [attendanceRecords, currentUser]);

  const handleRefreshAttendance = React.useCallback(() => {
    const today = new Date().toISOString().split("T")[0];
    const tenDaysAgo = new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];
    dispatch(
      fetchAttendanceRecords({
        page: 1,
        limit: 200,
        from_date: tenDaysAgo,
        to_date: today,
      }),
    ).catch(() => {});
  }, [dispatch]);

  const showPendingApprovals =
    (hasLeaveFeature && leaves.length > 0) ||
    (hasEmployeesFeature && requisitions.length > 0);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        maxWidth: 1400,
        width: "100%",
        mx: "auto",
        p: { xs: 2, sm: 2.5, md: 3 },
        boxSizing: "border-box",
      }}
    >
      {/* ===== ROW 1: Banner + Employee Info (employees only) ===== */}
      {isEmployeeRole ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1fr) 320px",
            },
            gap: 2.5,
            mb: 2.5,
            alignItems: "stretch",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <HeaderBanner
              user={currentUser}
              userRole={userRole}
              attendanceCount={
                attendanceRecords.filter((r) => r.check_in).length
              }
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {employeeCompany && <EmployeeInfoCard company={employeeCompany} />}
          </Box>
        </Box>
      ) : (
        <HeaderBanner
          user={currentUser}
          userRole={userRole}
          attendanceCount={attendanceRecords.filter((r) => r.check_in).length}
        />
      )}

      {/* ===== ROW 2: Attendance / Break / Quick Actions (employees only) ===== */}
      {isEmployeeRole && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 0.9fr) minmax(0, 1.5fr) 320px",
            },
            gap: 2.5,
            mb: 3,
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              gridColumn: { xs: "1", md: "1 / span 2" },
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              "& > *": { minWidth: 0 },
            }}
          >
            {showMyAttendanceCard && (
              <MyAttendanceCard
                myTodayRecord={myTodayRecord}
                user={currentUser}
                onRefresh={handleRefreshAttendance}
                onStatusUpdate={setAttendanceStatus}
                bottomRight={
                  hasProjectsFeature ? (
                    <Stack spacing={2.5}>
                      <TaskStatsWidget twoPerRow />
                      <ActiveProjectsWidget />
                    </Stack>
                  ) : null
                }
              />
            )}
          </Box>

          <Box sx={{ minWidth: 0, gridColumn: { xs: "1", md: "3" } }}>
            <Stack spacing={2.5}>
              <EmployeeQuickActionsCard isFeatureEnabled={isFeatureEnabled} />
              {hasLeaveFeature && (
                <UpcomingHolidaysWidget compact orangish />
              )}{" "}
            </Stack>
          </Box>
        </Box>
      )}

      {/* ===== ROW 3: Top Priority Tasks & Pending Approvals (employees only) ===== */}
      {isEmployeeRole && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1fr) minmax(0, 1fr)", // 50% / 50% split
            },
            gap: 2.5, // same gap as the cards above
            mb: 3,
            alignItems: "start",
          }}
        >
          {hasProjectsFeature ? (
            <TopPriorityTasks tasks={topPriorityTasks} userRole={userRole} />
          ) : (
            <Box /> // Empty placeholder to keep grid alignment if feature disabled
          )}

          {showPendingApprovals ? (
            <PendingApprovalsWidget
              userRole={userRole}
              leaves={hasLeaveFeature ? leaves : []}
              requisitions={hasEmployeesFeature ? requisitions : []}
            />
          ) : (
            <Box />
          )}
        </Box>
      )}

      {/* 3. Quick Shortcuts (Company/Admin only) */}
      {!isEmployeeRole && (
        <QuickShortcutsBar
          userRole={userRole}
          hasPermission={hasPermission}
          isFeatureEnabled={isFeatureEnabled}
        />
      )}

      {/* Idle Without Task Alert */}
      {isEmployeeRole && attendanceStatus?.isIdleWithoutTask && (
        <Paper
          component={motion.div}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 3,
            borderRadius: "6px",
            background: isDark
              ? "linear-gradient(135deg, rgba(239, 68, 68, 0.18) 0%, rgba(245, 158, 11, 0.12) 100%)"
              : "linear-gradient(135deg, #fff1f2 0%, #fffbeb 100%)",
            border: "1.5px solid",
            borderColor: isDark ? alpha("#ef4444", 0.45) : "#fca5a5",
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "6px",
                bgcolor: isDark ? alpha("#ef4444", 0.3) : "#ffe4e6",
                color: "#e11d48",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MdTimer size={26} />
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  color: isDark ? "#fca5a5" : "#9f1239",
                  lineHeight: 1.2,
                  mb: 0.5,
                }}
              >
                You are not doing any task
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? alpha("#ffffff", 0.8) : "#881337",
                  fontWeight: 500,
                  fontSize: "0.85rem",
                }}
              >
                You will be in break in 5 minutes unless you start or resume a
                task.
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            onClick={() => navigate("/company/tasks")}
            endIcon={<MdArrowForward />}
            sx={{
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              fontWeight: 800,
              textTransform: "none",
              px: 2.5,
              py: 1,
              borderRadius: "6px",
            }}
          >
            Go to Tasks
          </Button>
        </Paper>
      )}

      {/* 4. Main Dashboard Grid (Company / Admin only) */}
      {!isEmployeeRole && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1fr) minmax(0, 1fr)",
            },
            gap: 3,
            width: "100%",
            boxSizing: "border-box",
            alignItems: "start",
          }}
        >
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <Stack spacing={3}>
              {hasProjectsFeature && (
                <TopPriorityTasks
                  tasks={topPriorityTasks}
                  userRole={userRole}
                />
              )}
              {showPendingApprovals && (
                <PendingApprovalsWidget
                  userRole={userRole}
                  leaves={hasLeaveFeature ? leaves : []}
                  requisitions={hasEmployeesFeature ? requisitions : []}
                />
              )}
            </Stack>
          </Box>
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <Stack spacing={3}>
              {showAttendanceQuickView && !showMyAttendanceCard && (
                <AttendanceQuickView
                  attendanceRecords={attendanceRecords}
                  totalEmployees={employees.length}
                  loading={attendanceLoading}
                />
              )}
              {hasLeaveFeature && !isEmployeeRole && <UpcomingHolidaysWidget />}
            </Stack>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CompanyDashboard;
